<template>
  <div class="row jobseeker-details" v-if="profile.jobSeeker">
    <RedirectLink
      v-if="applicant && jobId"
      :to="`/employees/jobs-view/${$route.params.jobId}`"
    />
    <RedirectLink v-else />
    <div class="contact-info-wrapper">
      <div class="contact-info">
        <div class="profile-img">
          <img :src="signedUrl" alt="" />
        </div>
        <div class="contact-info-details">
          <h3>
            {{
              hasProfileViewAccess || !applicant
                ? `${profile.name} ${profile.lastName}`
                : `Jobseeker ${profile._id.slice(0, 4)}`
            }}
          </h3>
          <span><PhMapPin weight="fill" size="24px" />{{ locationText }}</span>
          <span
            ><PhPhone weight="fill" size="24px" />{{
              hasProfileViewAccess || !applicant
                ? profile.phoneNumber || "Not provided"
                : "**********"
            }}</span
          >
          <span
            ><PhEnvelope weight="fill" size="24px" />{{
              hasProfileViewAccess || !applicant ? profile.email : "**********"
            }}</span
          >
        </div>
      </div>
      <div
        :class="['warning', { 'rejected-job': rejectedJobInfo }]"
        v-if="
          applicant &&
          !hasProfileViewAccess &&
          isMatchDetailView &&
          !fromMatchesView
        "
      >
        <div class="warning-text">
          <div class="lock-icon">
            <img v-if="!rejectedJobInfo" src="/img/icons/lock.svg" alt="" />
            <PhXCircle color="red" weight="fill" :size="32" v-else />
          </div>
          <template v-if="rejectedJobInfo">
            Job Declined On {{ rejectedJobInfo.formattedDate }}
          </template>
          <template v-else>
            Unlock to connect with, and access other information about this
            applicant
          </template>
        </div>
        <DefaultButton
          v-if="!rejectedJobInfo"
          id="unlock_applicant_click"
          :labelText="unlockButtonLabel"
          @click="$emit('request-profile-access')"
        />
      </div>
      <div
        :class="['warning', { 'sent-job': !showSend }]"
        v-if="isMatchDetailView && fromMatchesView"
      >
        <div class="warning-text" style="white-space: nowrap">
          <div class="lock-icon">
            <img src="/img/icons/send.svg" alt="" />
          </div>
          {{
            showSend
              ? "Invite this job seeker to apply to a job!"
              : "Job Sent on " + moment(sentDate).format("MMMM D, YYYY")
          }}
        </div>
        <DefaultButton
          v-if="showSend"
          labelText="Invite"
          @click="$emit('send-job')"
        />
      </div>
      <div :class="['warning']" v-if="isCompanyMatch && !paymentIntent">
        <div class="warning-text" style="white-space: nowrap">
          <div class="lock-icon">
            <img src="/img/icons/send.svg" alt="" />
          </div>
          Send job to this match
        </div>
        <DefaultButton
          v-if="isCompanyMatch"
          labelText="Send Job"
          @click="handleSendJobs"
        />
      </div>
      <div class="update-profile" v-if="isEditProfile">
        <div class="info-text">Edit your profile</div>
        <DefaultButton
          btnClass="apply-btn"
          labelText="Edit Profile"
          @click="$emit('click-update')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DefaultButton from "@/components/DefaultButton"
import RedirectLink from "@/components/RedirectLink"
import { PhMapPin, PhPhone, PhEnvelope } from "@phosphor-icons/vue"
import file from "@/api/file.js"
import moment from "moment" // ✅ for date formatting
import { PhXCircle } from "@phosphor-icons/vue"

export default {
  components: {
    DefaultButton,
    PhMapPin,
    PhPhone,
    PhEnvelope,
    RedirectLink,
    PhXCircle,
  },
  props: {
    profile: {
      type: Object,
      required: true,
    },
    applicant: {
      type: Boolean,
      default: false,
    },
    hasProfileViewAccess: {
      type: Boolean,
      default: false,
    },
    isEditProfile: {
      type: Boolean,
      default: false,
    },
    showSend: {
      type: Boolean,
      default: true,
    },
    sentDate: {
      type: String,
      default: "",
    },
    product: {
      type: Object,
      default: null,
    },
  },
  created() {
    const currentPath = this.$route.name
    if (
      currentPath === "jobMatchDetail" ||
      currentPath === "searchProfileJobApplication"
    ) {
      this.isMatchDetailView = true
    }

    this.fromMatchesView = this.$route.query.isMatch === "true"
  },
  mounted() {
    const companyMatch = this.$route.query.companyMatch
    this.isCompanyMatch = companyMatch
    this.loadFile()
  },
  data() {
    const jobId = this.$route.params.jobId
    const profileId = this.$route.params.profileId
    const paymentIntent = this.$route.query?.payment_intent
    return {
      isMatchDetailView: false,
      fromMatchesView: false,
      isCompanyMatch: false,
      jobId,
      profileId,
      paymentIntent,
      signedUrl:
        typeof this.file === "string"
          ? this.file
          : "/img/icons/user-profile.svg",
    }
  },
  computed: {
    locationText() {
      const { city, state, zip } = this.profile
      if (!city && !state) return zip || ""
      return `${city}, ${state} ${zip || ""}`.trim()
    },
    unlockButtonLabel() {
      // Free unlock credits (GFB-1427), set on the product by the page.
      const free = this.product?.freeUnlock
      if (free?.remaining) {
        return `Unlock free (${free.remaining} left)`
      }
      return `Unlock for $${this.product ? this.product.amount / 100 : "XX.XX"}`
    },
    rejectedJobInfo() {
      try {
        const storedData = this.profile
        if (!storedData || !Array.isArray(storedData.profilesRejected))
          return null

        const declined = storedData.profilesRejected.find(
          (item) => item.job === this.jobId
        )

        if (declined) {
          // If declined.date exists, use it
          if (declined.date) {
            return {
              ...declined,
              formattedDate: moment(declined.date).format("MMM DD, YYYY"),
            }
          } else if (declined.ends) {
            const oneWeekMs = 7 * 24 * 60 * 60 * 1000
            let endsDateObj
            if (typeof declined.ends === "string") {
              // Parse ISO string
              endsDateObj = new Date(declined.ends)
            } else {
              // Assume timestamp
              endsDateObj = new Date(declined.ends)
            }
            // Subtract one week
            const endDate = new Date(endsDateObj.getTime() - oneWeekMs)
            return {
              ...declined,
              formattedDate: moment(endDate).format("MMM DD, YYYY"),
            }
          }
        }
      } catch (error) {
        console.error("Error reading profilesRejected:", error)
      }
      return null
    },
  },
  methods: {
    handleSendJobs() {
      this.$bvModal.show("send-jobs-modal")
    },
    async loadFile() {
      try {
        const fileId = this.profile.profilePhoto?.id
        if (fileId) {
          const { data } = await file.getThumbnailUrl({
            id: fileId,
            type: "small",
          })
          if (data?.url) {
            this.signedUrl = data.url
          } else if (data?.errors) {
            console.error(data.errors[0])
          }
        } else if (typeof this.file === "string") {
          this.signedUrl = this.file
        } else {
          this.signedUrl = "/img/icons/user-profile.svg"
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error(error)
        }
      }
    },
  },
  watch: {
    profile: {
      deep: true,
      handler() {
        this.loadFile()
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.jobseeker-details {
  display: flex;
  padding: 24px 96px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background: var(--Gray-50, #f6f7f9);
  margin: 0;

  .contact-info-wrapper {
    display: flex;
    width: 100%;

    .contact-info {
      display: flex;
      width: 100%;
      align-items: center;

      .profile-img {
        fill: var(--Gray-100, #eceef2);
        border-radius: 50%;
        margin-right: 32px;

        img {
          border-radius: 10%;
          width: 100px;
          height: 100px;
          object-fit: contain;
          max-width: unset !important;
        }
      }

      .contact-info-details {
        display: flex;
        flex-direction: column;

        h3 {
          margin-bottom: 6px;
          font-size: 24px;
          font-weight: 700;
        }

        span {
          display: flex;
          margin-top: 12px;
          font-size: 14px;
          white-space: normal !important;
          word-break: break-word;
          align-items: center;
          gap: 6px;

          svg {
            margin-right: 5px;
          }
        }
      }
    }
  }

  .warning {
    display: flex;
    padding: 20px 24px 24px 24px;
    flex-direction: column;
    align-items: center;
    border-radius: 8px;
    border: 1px solid var(--Blue-50, #d6e1f3);
    background: var(--White, #fff);

    &.sent-job {
      flex-direction: unset;

      .warning-text {
        margin-bottom: 0;
      }
    }

    .warning-text {
      margin-bottom: 16px;
      display: flex;
      align-items: center;

      .lock-icon {
        margin-right: 16px;

        img {
          max-width: unset !important;
        }
      }
    }
  }

  .warning.rejected-job {
    display: flex;
    padding: 20px 24px 24px 24px;
    flex-direction: row;
    align-items: center;
    border-radius: 8px;
    border: 1px solid var(--Blue-50, #d6e1f3);
    background: var(--White, #fff);
    white-space: nowrap;

    .warning-text {
      margin-bottom: 0;
      font-size: 14px;
      font-weight: 600;
    }
  }
}

.update-profile {
  display: flex;
  padding: 20px 24px 24px 24px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid var(--Gray-200, #d5d9e2);
  background: var(--White, #fff);
  min-width: 350px;
  justify-content: center;

  .info-text {
    color: var(--Gray-900, #343946);
    text-align: center;
    font-size: 16px;
    font-weight: 600;
  }

  .button-wrapper {
    width: 100% !important;
  }
}

@media (max-width: 1279px) {
  .contact-info-wrapper,
  .contact-info {
    flex-direction: column;
    gap: 16px;
  }

  .contact-info-wrapper .contact-info .contact-info-details span {
    white-space: nowrap;
  }

  .contact-info-wrapper .contact-info .contact-info-details h3 {
    text-align: center;
  }

  .update-profile {
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .jobseeker-details {
    padding: 10px;

    .contact-info-wrapper {
      .contact-info .profile-img {
        margin-right: 0px;
      }
    }
  }
}
</style>
